import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PillButton } from "@/components/sources/shared";
import { api, formatApiError } from "@/lib/api";

export const ROLE_LABEL = { admin: "Administrator", guest: "Member" };

export const LibrarySelect = ({ value, onChange, owners, testid, disabled }) => (
  <Select value={value || "own"} onValueChange={(v) => onChange(v === "own" ? "" : v)} disabled={disabled}>
    <SelectTrigger className="h-9 w-56 bg-white text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="own" data-testid={`${testid}-option-own`}>Their own library</SelectItem>
      {owners.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`${testid}-option-${o.id}`}>Watch {o.name}'s library ({o.videos})</SelectItem>)}
    </SelectContent>
  </Select>
);

const Field = ({ id, label, children }) => <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>;

export const AddUserDialog = ({ open, onOpenChange, owners, defaultOwner, onDone }) => {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "guest", shared_from: defaultOwner || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", { ...f, shared_from: f.shared_from || null });
      toast.success(`Account created for ${f.email}`);
      setF({ name: "", email: "", password: "", role: "guest", shared_from: defaultOwner || "" });
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle>Add a family member</DialogTitle>
          <DialogDescription>They sign in with this email and password. Share your library so they don't need their own server.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field id="nu-name" label="Name"><Input id="nu-name" value={f.name} onChange={set("name")} data-testid="add-user-name-input" /></Field>
          <Field id="nu-email" label="Email"><Input id="nu-email" type="email" required value={f.email} onChange={set("email")} data-testid="add-user-email-input" /></Field>
          <Field id="nu-pw" label="Password"><Input id="nu-pw" type="text" required minLength={6} value={f.password} onChange={set("password")} className="font-mono" data-testid="add-user-password-input" /></Field>
          <div className="flex flex-wrap gap-4">
            <Field id="nu-role" label="Role">
              <Select value={f.role} onValueChange={set("role")}>
                <SelectTrigger className="h-9 w-40 bg-white text-xs" data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`add-user-role-option-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field id="nu-lib" label="Library"><LibrarySelect value={f.shared_from} onChange={set("shared_from")} owners={owners} testid="add-user-library-select" /></Field>
          </div>
          <PillButton type="submit" disabled={busy} testid="add-user-submit">{busy && <Loader2 size={14} className="animate-spin" />}Create account</PillButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PasswordDialog = ({ user, onOpenChange }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/password`, { password: pw });
      toast.success(`Password updated for ${user.email}`);
      setPw("");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>{user?.email} will sign in with the new password from now on.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field id="rp" label="New password"><Input id="rp" type="text" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="font-mono" data-testid="reset-password-input" /></Field>
          <PillButton type="submit" disabled={busy || pw.length < 6} testid="reset-password-submit">{busy && <Loader2 size={14} className="animate-spin" />}Set password</PillButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
